import React from 'react';
import PropTypes from 'prop-types';
import '../style/updateTab.css';
import PreviousApplicationUpdate from "./previousApplicationUpdate";
import moment from 'moment';
import PolicyTabUpdateQuestion from "./ApplicationPolicy/policyTabUpdateQuestion";
import { isAragUser, isSolicitorUser, reduceAnswers } from "../helpers/functions";
import { isCanada, ARAG_USER_CAN_SUBMIT_UPDATE_FORM, DROPZONE_ACCEPTED_FILES_ARRAY } from "../helpers/variables";
import { UPLOAD_FILE_POLICY_UPDATE_FORM_CA_TOOLTIP, SUBMIT,
    PRIMARY, TERTIARY, DOTS, ERROR, SUCCESS, RIGHT, ROLE_SOLICITOR,
    REJECTED_OR_DECLINED_STATUSES,
    MOMENT_DATE_TIME_FORMAT, HEADING_3,
    MEDIUM, REQUEST_UPDATE, DOTTED, HEADING_4, SIZE_LG, WARNING
} from "../helpers/constants";
import _ from 'lodash';
import { FormGroup, Alert, Button, Loader, Heading, HorizontalRule, List, Link, DragAndDrop } from 'arag-ui-lib';
import { FORM_GROUP_VARIANTS } from '../helpers/formGroupVariants';
import CONTENT_STRINGS from '../helpers/contentStrings';
import ModalWrapper from '../helpers/modalWrapper';
import { sanitizeInfoText } from '../helpers/commentParser';
import AttachmentsList from "./General/attachmentsList";

const UpdateTab = (props) => {
    const {
        Store,
        userRoles,
        UserRole,
        applicationStatusMachineName,
        HandleSubmit,
        HandleInputChange,
        HandleRemoveAttachment,
        shouldUpdateQuestionBeMandatory,
        OnDrop,
        onDropMultiple,
        onDropSingle,
        HandleFormCompChange,
        HandleDateChange,
        HandleSelectChange,
        hasProcessingAd,
        pigApplied
    } = props;

    // Extract common conditions
    const canSubmitUpdate = UserRole === ROLE_SOLICITOR || (isAragUser(userRoles) && ARAG_USER_CAN_SUBMIT_UPDATE_FORM);
    const isNotRejectedOrDeclined = !REJECTED_OR_DECLINED_STATUSES.includes(applicationStatusMachineName);
    const hasAttachments = Store.attachments.length > 0;

    if (Store.fetching || Store.fetchingLastUpdate || Store.fetchingAllPreviousUpdates) {
        return (
            <div className="col-sm-12 updateTab-Wrapper">
                <Loader variant={DOTS} dotSize={MEDIUM} />
            </div>
        )
    }

    if (Store.failedToGetQuestions) {
        return <Alert variant={ERROR} label={Store.failedToGetQuestions} />;
    }
    
    return (
        <div className="col-sm-12 updateTab-Wrapper">
            {/* Last update message */}
            {Store.updatePolicy.dateTime && isSolicitorUser(userRoles)
            ?
                <div className="noprintarea">
                    <Heading 
                        variant={HEADING_3} 
                        title={`${CONTENT_STRINGS.LAST_UPDATE_DETAILS} ${moment(Store.updatePolicy.dateTime)
                            .format(MOMENT_DATE_TIME_FORMAT)}. ${CONTENT_STRINGS.LAST_UPDATE_INSTRUCTIONS}`}
                        headingTestId={'policy-update-form-instruction'}
                    />
                    <HorizontalRule variant={DOTTED} />
                </div>
            :
                null
            }
            {
                hasProcessingAd
                ?
                    <Alert variant={WARNING} label={CONTENT_STRINGS.AD_PROCESSING_UPDATE_FORM_BANNER} />
                :
                    null
            }

            {/* Main form */}
            {isNotRejectedOrDeclined
            ?
                <form className="form-horizontal noprintarea" onSubmit={(evt) => HandleSubmit(evt, '')}>
                    {/* Questions and file upload section */}
                    {canSubmitUpdate
                    ?
                        <>
                            {_.sortBy(Store.questions, ["sortOrder"]).map(question => 
                                question.isTopLevel
                                ?
                                    <PolicyTabUpdateQuestion
                                        key={question.id}
                                        shouldUpdateQuestionBeMandatory={shouldUpdateQuestionBeMandatory}
                                        allQuestions={Store.questions}
                                        question={question}
                                        formData={Store.formData}
                                        HandleFormCompChange={HandleFormCompChange}
                                        HandleDateChange={HandleDateChange}
                                        OnDrop={OnDrop}
                                        onDropMultiple={onDropMultiple}
                                        onDropSingle={onDropSingle}
                                        HandleSelectChange={HandleSelectChange}
                                        pigApplied={pigApplied}
                                    />
                                :
                                    null
                            )}

                            {/* File upload */}
                            <div className="form-group" data-testid="upload-file">
                                <label className="col-sm-4 control-label">{CONTENT_STRINGS.UPLOAD_FILE_UPDATE_FORM}</label>
                                <div className="col-sm-6">
                                    <DragAndDrop
                                        label={Store.uploadLabel}
                                        accepts={DROPZONE_ACCEPTED_FILES_ARRAY}
                                        allowMultiple={true}
                                        onDrop={OnDrop}
                                        ddTestId={'policy-update-DragAndDrop'}
                                    />
                                    {isCanada
                                    ?
                                        <span className="help-block">{sanitizeInfoText(UPLOAD_FILE_POLICY_UPDATE_FORM_CA_TOOLTIP)}</span>
                                    :
                                        null
                                    }
                                    <AttachmentsList
                                        attachments={Store.attachments || []}
                                        onAttachmentRemove={(attachment, index) => HandleRemoveAttachment(attachment, index)}
                                    />
                                </div>
                            </div>
                            {/* Tags input */}
                            <FormGroup
                                labelSize="col-sm-4"
                                inputSize="col-sm-6"
                                label={CONTENT_STRINGS.ADD_TAGS}
                                variant={FORM_GROUP_VARIANTS.TEXT}
                                inputProps={{
                                    onChange: (e) => HandleInputChange(e.target.name, e.target.value),
                                    name: 'tags',
                                    value: Store.tags,
                                    disabled: !hasAttachments
                                }}
                                formGroupTestId={"add-tags"}
                            />
                            <FormGroup
                                labelSize={"col-sm-9"}
                                inputSize={"col-sm-9"}
                                label={Store.tagsDescription}
                                variant={FORM_GROUP_VARIANTS.PLAIN}
                                inputProps={{
                                    name: 'tagsDescription'
                                }}
                                formGroupTestId={'policy-update-tag-instruction'}
                            />

                            {/* Submit buttons */}
                            <div className="form-group">
                                <div className="col-sm-5"> </div>
                                <div className="submit-buttons">
                                    <Button
                                        variant={TERTIARY}
                                        label={CONTENT_STRINGS.NO_CHANGES}
                                        arrowDirection={RIGHT}
                                        onClick={(evt) => HandleSubmit(evt, CONTENT_STRINGS.NO_CHANGES)}
                                        buttonTestId={"policy-update-no-changes"}
                                        disabled={Store.posting}
                                    />
                                    <Button
                                        variant={PRIMARY}
                                        label={CONTENT_STRINGS.UPDATE}
                                        type={SUBMIT}
                                        arrowDirection={RIGHT}
                                        buttonTestId={"policy-update-submit"}
                                        disabled={Store.posting}
                                    />
                                </div>
                            </div>
                        </>
                    :
                        null
                    }
                    {/* Loader for main form */}
                    {Store.posting ? <Loader variant={DOTS} dotSize={MEDIUM} /> : null}
                    {/* Request update section for regular users */}
                    {isAragUser(userRoles)
                    ?
                        <div className="noprintarea">
                            <form className="form-horizontal">
                                <div className="form-group">
                                    <div className="col-sm-5">
                                        <Button
                                            onClick={(evt) => HandleSubmit(evt, REQUEST_UPDATE)}
                                            variant={PRIMARY}
                                            arrowDirection={RIGHT}
                                            label={CONTENT_STRINGS.REQUEST_UPDATE}
                                            buttonTestId={"policy-update-request-update"}
                                            disabled={Store.postingRequestUpdate}
                                        />
                                    </div>
                                </div>
                                {Store.postingRequestUpdate ? <Loader variant={DOTS} dotSize={MEDIUM} /> : null}
                            </form>
                        </div>
                    :
                        null
                    }
                    {/* Error message */}
                    {Store.badMsg
                    ?
                        <div className="col-sm-12">
                            <div className="col-sm-4" />
                            <div className="col-sm-6">
                                <Alert variant={ERROR} label={Store.badMsg} />
                            </div>
                        </div>
                    :
                        null
                    }
                    {/* Success message */}
                    {Store.msg
                    ?
                        <div>
                            <div className="col-sm-4" />
                            <div className="col-sm-6">
                                <Alert variant={SUCCESS} label={Store.msg} />
                            </div>
                        </div>
                    :
                        null
                    }
                </form>
            :
                null
            }

            {/* Previous updates section */}
            {Store.previousUpdates.length > 0
            ?
                <div className="row">
                    <div className="col-sm-12 noprintarea">
                        <HorizontalRule variant={DOTTED}/>
                    </div>
                    <div className="col-sm-12 noprintarea">
                        <Heading 
                            variant={HEADING_4}
                            title={CONTENT_STRINGS.PREVIOUS_UPDATES} 
                            headingTestId="policy-previous-updates-header"
                        />
                    </div>
                    <div className="col-sm-12 noprintarea">
                        <List
                            id="applicationPolicy-update-tab-PreviousUpdates-list"
                            items={Store.previousUpdates.map((update, index) => ({
                                content: (
                                    <Link
                                        variant={TERTIARY} 
                                        key={index} 
                                        onClick={() => {
                                            HandleInputChange('openModal', true);
                                            HandleInputChange('selectedPreviousUpdate', update);
                                        }}
                                        label={moment(update.dateTime).format(MOMENT_DATE_TIME_FORMAT)}
                                        linkTestId={`policy-previous-update-${index}`}
                                    />
                                ),
                            }))}
                        />
                    </div>
                    <ModalWrapper
                        isOpen={Store.openModal}
                        onClose={() => HandleInputChange('openModal', false)}
                        buttonTestId={"policy-previous-update-close"}
                        modalTestId={"policy-previous-update-modal"}
                        title={CONTENT_STRINGS.PREVIOUS_UPDATE_DETAILS}
                        size={SIZE_LG}
                    >
                        <PreviousApplicationUpdate
                            Date={moment(Store.selectedPreviousUpdate?.dateTime).format(MOMENT_DATE_TIME_FORMAT)}
                            Questions={Store.questions}
                            PreviousUpdate={Store.selectedPreviousUpdate}
                            answers={reduceAnswers(Store.selectedPreviousUpdate?.policyUpdateAnswersDtos) || {}}
                        />
                    </ModalWrapper>
                </div>
            :
                null
            }
        </div>
    );
};

UpdateTab.propTypes = {
    Store: PropTypes.shape({
        fetching: PropTypes.bool,
        fetchingLastUpdate: PropTypes.bool,
        fetchingAllPreviousUpdates: PropTypes.bool,
        failedToGetQuestions: PropTypes.string,
        updatePolicy: PropTypes.shape({
            dateTime: PropTypes.string
        }),
        questions: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            isTopLevel: PropTypes.bool,
            sortOrder: PropTypes.number
        })),
        formData: PropTypes.object,
        uploadLabel: PropTypes.string,
        attachments: PropTypes.array,
        tags: PropTypes.string,
        tagsDescription: PropTypes.string,
        posting: PropTypes.bool,
        postingRequestUpdate: PropTypes.bool,
        badMsg: PropTypes.string,
        msg: PropTypes.string,
        previousUpdates: PropTypes.arrayOf(PropTypes.shape({
            dateTime: PropTypes.string,
            policyUpdateAnswersDtos: PropTypes.array
        })),
        openModal: PropTypes.bool,
        selectedPreviousUpdate: PropTypes.shape({
            dateTime: PropTypes.string,
            policyUpdateAnswersDtos: PropTypes.array
        })
    }).isRequired,
    userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
    UserRole: PropTypes.string.isRequired,
    applicationStatusMachineName: PropTypes.string.isRequired,
    HandleSubmit: PropTypes.func.isRequired,
    HandleInputChange: PropTypes.func.isRequired,
    HandleRemoveAttachment: PropTypes.func.isRequired,
    shouldUpdateQuestionBeMandatory: PropTypes.func.isRequired,
    OnDrop: PropTypes.func.isRequired,
    onDropMultiple: PropTypes.func.isRequired,
    onDropSingle: PropTypes.func.isRequired,
    HandleFormCompChange: PropTypes.func.isRequired,
    HandleDateChange: PropTypes.func.isRequired,
    HandleSelectChange: PropTypes.func.isRequired,
    hasProcessingAd: PropTypes.bool,
    pigApplied: PropTypes.bool
};

export default UpdateTab;